import _ from 'lodash';
import initialize, { handleLogout } from '@patsnap-utils/passport';
import { psAxios, setPostByFormPassport } from '@patsnap/common-ps-http';
import { transformUrlProxy, SLD, Site } from '@patsnap-utils/biz';
import { sleep } from '@telecom/shared/utils/task';
import type { AxiosError } from 'axios';
import type { ISyncTokenParams } from '@patsnap-utils/passport/src/type';
import { injectService } from '@patsnap/tahiti-core';
import { UnauthenticatedHandler } from '../services/unauthenticated-handler/unauthenticated-handler';
import {
    injectFingerprintInterceptor,
    injectProjectRequestInterceptor,
    injectSignInterceptor,
    injectPassportInterceptor,
    setProjectRequestHeaders,
} from './interceptors';
import { prepareTokenDeferred } from './defer';

export interface IProjectPreset {
    name: string
}

const PASSPORT_URI = import.meta.env.VITE_PASSPORT_URI;
const CLIENT_ID = import.meta.env.VITE_PASSPORT_CLIENT_ID;
const CLIENT_NAME = import.meta.env.VITE_PASSPORT_CLIENT_NAME;

const baseAuthServer = transformUrlProxy(SLD.PASSPORT, PASSPORT_URI as string);

function stopper() {
    if (window.location.search.indexOf('noraven') > -1) {
        throw new Error('Local stop redirect');
    }
}

function revertString(str:string) {
    return str.split('').reverse().join('');
}

function redirectToUserSettings(page: string, error: any) {
    const urlParams = {
        from: CLIENT_NAME,
        client_id: CLIENT_ID,
        redirect_uri: encodeURIComponent(window.location.href),
        errorCode: _.get(error, 'errcode'),
    };
    const urlSearchParams = new URLSearchParams(urlParams);
    const url = new Site({
        sld: SLD.ACCOUNT,
        pathname: `user-settings/#/${page}?${urlSearchParams.toString()}`,
    }).href;
    stopper();
    window.location.replace(url);
}

function createInitializeConfig() {
    return {
        baseAuthServer,
        clientId: CLIENT_ID,
        clientName: CLIENT_NAME,
        redirectToLogin(error: AxiosError) {
            const unauthenticatedHandler = injectService<UnauthenticatedHandler>(UnauthenticatedHandler);
            unauthenticatedHandler.handle(error);
            // 正在跳转（大概6秒够了），后面的别执行了
            return sleep(6000);
        },
        /*  可选，允许修改 check session 的参数 */
        syncTokenParamsModifier(params: ISyncTokenParams) {
            return { ...params /* , flag: 'analytics', */ };
        },
        detectResponseAsUnauthorized(error: AxiosError) {
            const redirectUrl = _.get(error, 'response.data.redirectUrl');
            if (redirectUrl) {
                stopper();
                window.location.href = redirectUrl;
                throw error;
            }
            return _.get(error, 'response.status') === 401;
        },
        onPaymentRequired(error: any) {
            redirectToUserSettings('upper-limit', error);
        },
        async onMfaRequired(error: any) {
            redirectToUserSettings('mfa-required', error);
        },
    };
}

async function prepareTokenBase() {
    injectFingerprintInterceptor();
    injectProjectRequestInterceptor();
    injectSignInterceptor();
    /* 清除 csrfTokenInterceptor */
    psAxios.interceptors.request.eject(psAxios.psInterceptors.csrfTokenInterceptor);
    injectPassportInterceptor();

    window.customBeforeLogout = function customBeforeLogout() {
        // 此方法默认会被 Header V4点"登出"时调用， 用其他头时，登出时自己调用 handleLogout()
        return handleLogout();
    };

    const initializeConfig = createInitializeConfig();
    const passport = await initialize(initializeConfig as any);

    // 下面的代码不能移除,和请求签名相关
    _.set(window, revertString('6csg9md32h'), function() {
        return passport.getToken('token') || '';
    });
    // 登录的用户才注册passport的拦截器
    setPostByFormPassport(passport);

    return passport;
}

/**
 * 更新 token base 请求的 header， 如 X-Site-Lang
 * @param headers
 */
function updateTokenBaseRequestHeaders(headers: Record<string, unknown>): void {
    setProjectRequestHeaders(headers);
}


export {
    prepareTokenBase,
    updateTokenBaseRequestHeaders,
};
